import { useEffect, useState } from "react";
import {
  useSupport,
  selectLists,
  refreshList,
  setMyStatus,
  runSessionAction,
} from "../../hooks/supportStore";
import { timeAgo, useNow, showSupportError } from "./support-helpers";
import ApiService from "../../api/apiService";

function SessionRow({ session, selected, unread, now, onOpen, onAccept, accepting }) {
  const waiting = session.status === "waiting";
  const subtitle = waiting
    ? `Waiting ${timeAgo(session.queued_at, now)}${session.queue_position ? ` · #${session.queue_position}` : ""}`
    : session.last_message_preview || session.escalate_reason || "";

  return (
    <li>
      <div
        className={`cs-row${selected ? " cs-row--selected" : ""}`}
        role="button"
        tabIndex={0}
        onClick={() => onOpen(session.id)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onOpen(session.id);
          }
        }}
      >
        <div className="cs-row__main">
          <div className="cs-row__title">
            <span className="cs-row__name">{session.customer_name}</span>
            {session.customer_verified ? (
              <span className="cs-tag cs-tag--verified">Verified</span>
            ) : (
              <span className="cs-tag">Guest</span>
            )}
          </div>
          <div className="cs-row__sub">{subtitle}</div>
          {!waiting && session.agents?.length > 0 && (
            <div className="cs-row__agents">{session.agents.map((a) => a.display_name).join(", ")}</div>
          )}
        </div>
        <div className="cs-row__side">
          {!waiting && (
            <span className="cs-row__time">{timeAgo(session.last_message_at || session.queued_at, now)}</span>
          )}
          {unread > 0 && <span className="cs-badge">{unread > 99 ? "99+" : unread}</span>}
          {waiting && onAccept && (
            <button
              type="button"
              className="cs-btn cs-btn--primary cs-btn--sm"
              disabled={accepting}
              onClick={(e) => {
                e.stopPropagation();
                onAccept(session.id);
              }}
            >
              {accepting ? "…" : "Accept"}
            </button>
          )}
        </div>
      </div>
    </li>
  );
}

function Section({ title, count, children, empty, defaultOpen = true, onToggle }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <section className="cs-section">
      <button
        type="button"
        className="cs-section__head"
        aria-expanded={open}
        onClick={() => {
          setOpen(!open);
          onToggle?.(!open);
        }}
      >
        <i className={`fa fa-caret-${open ? "down" : "right"}`} aria-hidden />
        <span>{title}</span>
        {typeof count === "number" && <span className="cs-section__count">{count}</span>}
      </button>
      {open && (count === 0 && empty ? <p className="cs-empty">{empty}</p> : <ul className="cs-list">{children}</ul>)}
    </section>
  );
}

function SupervisorStats() {
  const [stats, setStats] = useState(null);
  useEffect(() => {
    let alive = true;
    const load = () =>
      ApiService.support.getStats().then((res) => alive && res.success && setStats(res.stats));
    load();
    const timer = setInterval(load, 60000);
    return () => {
      alive = false;
      clearInterval(timer);
    };
  }, []);
  if (!stats) return null;
  const wait = stats.today.avg_wait_seconds;
  return (
    <div className="cs-stats" aria-label="Today's support stats">
      <div><strong>{stats.waiting}</strong><span>waiting</span></div>
      <div><strong>{stats.active}</strong><span>active</span></div>
      <div><strong>{stats.agents_available}</strong><span>agents on</span></div>
      <div><strong>{wait == null ? "–" : `${Math.round(wait / 60)}m`}</strong><span>avg wait</span></div>
      <div><strong>{stats.today.csat_average ?? "–"}</strong><span>CSAT</span></div>
    </div>
  );
}

function SupportInbox({ selectedId, onOpen, onShowAdmin }) {
  const support = useSupport();
  const now = useNow(30000);
  const { queue, mine, assisting, otherActive } = selectLists(support);
  const missed = support.missed.map((id) => support.sessions[id]).filter((s) => s?.status === "missed");
  const history = support.history.map((id) => support.sessions[id]).filter(Boolean);
  const [accepting, setAccepting] = useState(null);

  const accept = async (sessionId) => {
    setAccepting(sessionId);
    const res = await runSessionAction("claim", sessionId);
    setAccepting(null);
    if (!res.success) return showSupportError(res.message);
    onOpen(sessionId);
  };

  const row = (session, extra = {}) => (
    <SessionRow
      key={session.id}
      session={session}
      selected={session.id === selectedId}
      unread={support.unread[session.id] || 0}
      now={now}
      onOpen={onOpen}
      {...extra}
    />
  );

  return (
    <aside className="cs-inbox">
      <div className="cs-inbox__head">
        <h2>Customer Support</h2>
        <label className="cs-status">
          <span className={`cs-dot cs-dot--${support.connected ? support.myStatus : "offline"}`} aria-hidden />
          <select
            value={support.myStatus}
            onChange={(e) => setMyStatus(e.target.value)}
            disabled={!support.connected}
            aria-label="My availability"
          >
            <option value="online">Available</option>
            <option value="away">Away</option>
          </select>
        </label>
      </div>
      {!support.connected && <div className="cs-banner">Connecting to customer support…</div>}
      {support.me?.cs_supervisor && <SupervisorStats />}

      <div className="cs-inbox__scroll">
        <Section title="Queue" count={queue.length} empty="No customers waiting.">
          {queue.map((s) => row(s, { onAccept: accept, accepting: accepting === s.id }))}
        </Section>
        <Section title="My chats" count={mine.length} empty="You have no active chats.">
          {mine.map((s) => row(s))}
        </Section>
        {assisting.length > 0 && (
          <Section title="Assisting" count={assisting.length}>
            {assisting.map((s) => row(s))}
          </Section>
        )}
        <Section title="Team chats" count={otherActive.length} empty="No other active chats." defaultOpen={false}>
          {otherActive.map((s) => row(s))}
        </Section>
        <Section title="Missed" count={missed.length} empty="No missed chats." defaultOpen={false}>
          {missed.map((s) => row(s))}
        </Section>
        <Section
          title="History"
          empty="No closed chats yet."
          defaultOpen={false}
          onToggle={(open) => open && refreshList("history")}
        >
          {history.map((s) => row(s))}
        </Section>
      </div>

      {support.me?.admin_access && (
        <button type="button" className="cs-inbox__admin" onClick={onShowAdmin}>
          <i className="fa fa-users" aria-hidden /> Manage support agents
        </button>
      )}
    </aside>
  );
}

export default SupportInbox;
